import React, { useState, useEffect } from "react";
import { Calendar, Clock, Loader2 } from "lucide-react";
import AnimatePresence from "../../ui/AnimatePresence";
import { useGoogleCalendar } from "../../../contexts/useGoogleCalendar";
import { useUserPreferences } from "../../../hooks/useUserPreferences";
import { GoogleCalendarInfo } from "../../../types";

const DURATION_OPTIONS = [
  { value: 15, label: "15m" },
  { value: 30, label: "30m" },
  { value: 45, label: "45m" },
  { value: 60, label: "1h" },
];

const CalendarSettings: React.FC = () => {
  const { defaultTaskDuration, updateDefaultTaskDuration, timezone, updateTimezone } =
    useUserPreferences();
  const supportedValuesOf = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  const timeZones = supportedValuesOf ? supportedValuesOf("timeZone") : [];
  const {
    connection,
    isConnected,
    isLoading,
    connectGoogleCalendar,
    disconnectGoogleCalendar,
    toggleSync,
    fetchCalendarList,
    toggleCalendarVisibility,
    setHiddenCalendarIds,
  } = useGoogleCalendar();

  const [calendars, setCalendars] = useState<GoogleCalendarInfo[]>([]);
  const [isLoadingCalendars, setIsLoadingCalendars] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  useEffect(() => {
    if (isConnected) {
      setIsLoadingCalendars(true);
      fetchCalendarList()
        .then(setCalendars)
        .finally(() => setIsLoadingCalendars(false));
    }
  }, [isConnected, fetchCalendarList]);

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    await disconnectGoogleCalendar();
    setIsDisconnecting(false);
    setShowDisconnectConfirm(false);
    setCalendars([]);
  };

  const handleToggleAllCalendars = async () => {
    if (!connection) return;

    const nonJudyIds = nonJudyCalendars.map((cal) => cal.id);

    // Single atomic update: if all visible, hide them all; otherwise show them all.
    const newHiddenIds = allNonJudyVisible
      ? [
          ...connection.hidden_calendar_ids.filter(
            (id) => !nonJudyIds.includes(id)
          ),
          ...nonJudyIds,
        ]
      : connection.hidden_calendar_ids.filter(
          (id) => !nonJudyIds.includes(id)
        );

    await setHiddenCalendarIds(newHiddenIds);
  };

  // Check if all non-Judy calendars are visible
  const nonJudyCalendars = calendars.filter((cal) => !cal.isJudi);
  const allNonJudyVisible =
    nonJudyCalendars.length > 0 &&
    nonJudyCalendars.every(
      (cal) => !connection?.hidden_calendar_ids.includes(cal.id)
    );

  const formatRelativeTime = (dateStr: string | null) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60)
      return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24)
      return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-warm-text-tertiary" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-warm-text-primary mb-1">
          Calendar
        </h3>
        <p className="text-sm text-warm-text-secondary">
          Sync JuDI with your Google Calendar and configure scheduling defaults.
        </p>
      </div>

      {/* Google Calendar card */}
      <div className="p-4 rounded-lg border border-warm-border bg-warm-bg-elevated">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-warm-blue-light/20 dark:bg-warm-blue/20">
              <Calendar
                size={20}
                className="text-warm-blue-dark dark:text-warm-blue"
              />
            </div>
            <div>
              <div className="font-medium text-warm-text-primary">
                Google Calendar
              </div>
              {isConnected && connection && (
                <div className="text-xs text-warm-text-tertiary">
                  Connected since{" "}
                  {new Date(connection.connected_at).toLocaleDateString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    },
                  )}
                </div>
              )}
              {!isConnected && (
                <div className="text-xs text-warm-text-tertiary">
                  Sync your JuDI tasks to Google Calendar
                </div>
              )}
            </div>
          </div>

          {isConnected && (
            <button
              onClick={() => setShowDisconnectConfirm(true)}
              className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
            >
              Disconnect
            </button>
          )}
        </div>

        {!isConnected && (
          <button
            onClick={connectGoogleCalendar}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-warm-blue hover:bg-warm-blue-dark rounded-lg transition-colors"
          >
            <Calendar size={16} />
            Connect Google Calendar
          </button>
        )}

        {isConnected && connection && (
          <div className="space-y-4 mt-3">
            <div className="text-xs text-warm-text-tertiary">
              Last synced: {formatRelativeTime(connection.last_synced_at)}
            </div>

            {/* Sync toggle */}
            <div className="flex items-center justify-between py-2 border-t border-warm-border">
              <div>
                <div className="text-sm font-medium text-warm-text-primary">
                  Sync
                </div>
                <div className="text-xs text-warm-text-tertiary">
                  Auto-sync tasks with scheduled dates
                </div>
              </div>
              <button
                role="switch"
                aria-label="Sync"
                aria-checked={connection.sync_enabled}
                onClick={toggleSync}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  connection.sync_enabled
                    ? "bg-warm-blue"
                    : "bg-warm-border"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    connection.sync_enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Calendar list */}
            <div className="border-t border-warm-border pt-3">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-medium text-warm-text-primary">
                    Calendars to show
                  </div>
                  <div className="text-xs text-warm-text-tertiary">
                    Show these calendars in the Calendar view:
                  </div>
                </div>
              </div>

              {isLoadingCalendars ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="animate-spin text-warm-text-tertiary" size={18} />
                </div>
              ) : (
                <>
                  {nonJudyCalendars.length > 0 && (
                    <div className="flex items-center justify-between py-2 px-2 mb-2 bg-warm-bg-elevated rounded-lg">
                      <span className="text-sm text-warm-text-secondary">
                        Toggle all
                      </span>
                      <button
                        role="switch"
                        aria-checked={allNonJudyVisible}
                        aria-label="Toggle all calendars"
                        onClick={handleToggleAllCalendars}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          allNonJudyVisible
                            ? "bg-warm-blue"
                            : "bg-warm-border"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            allNonJudyVisible ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  )}
                  <div className="space-y-2">
                    {calendars.map((cal) => {
                      const isChecked =
                        cal.isJudi ||
                        !connection.hidden_calendar_ids.includes(cal.id);
                      return (
                        <label
                          key={cal.id}
                          className="flex items-center gap-3 py-1.5 cursor-pointer group"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={cal.isJudi}
                            onChange={() => toggleCalendarVisibility(cal.id)}
                            className="rounded border-warm-border text-warm-blue focus:ring-warm-blue disabled:opacity-50"
                          />
                          <span
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: cal.color }}
                          />
                          <span className="text-sm text-warm-text-secondary group-hover:text-warm-text-primary">
                            {cal.name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Calendar Defaults card */}
      <div className="p-4 rounded-lg border border-warm-border bg-warm-bg-elevated">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-lg bg-warm-blue-light/20 dark:bg-warm-blue/20">
            <Clock size={20} className="text-warm-blue-dark dark:text-warm-blue" />
          </div>
          <div>
            <div className="font-medium text-warm-text-primary">
              Default Task Duration
            </div>
            <div className="text-xs text-warm-text-tertiary">
              Duration assigned to tasks dropped onto the calendar. When
              connected to Google Calendar, this also sets the event length for
              synced events.
            </div>
          </div>
        </div>
        <div
          className="relative grid grid-cols-4 rounded-full p-1 bg-warm-bg-elevated border border-warm-border"
          role="group"
          aria-label="Default task duration"
        >
          {/* Sliding pill */}
          <div
            aria-hidden="true"
            className={`absolute top-1 bottom-1 w-[calc(25%-2px)] rounded-full bg-warm-blue shadow-soft-sm transition-[left] duration-200 ease-out ${
              defaultTaskDuration === 15
                ? "left-1"
                : defaultTaskDuration === 30
                  ? "left-[25%]"
                  : defaultTaskDuration === 45
                    ? "left-[50%]"
                    : "left-[75%]"
            }`}
          />
          {DURATION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => updateDefaultTaskDuration(opt.value)}
              aria-pressed={defaultTaskDuration === opt.value}
              className={`relative z-10 flex items-center justify-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium transition-colors duration-150 ease-in-out ${
                defaultTaskDuration === opt.value
                  ? "text-white"
                  : "text-warm-text-secondary hover:text-warm-text-primary"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 rounded-lg border border-warm-border bg-warm-bg-elevated">
        <label
          htmlFor="judi-time-zone"
          className="block font-medium text-warm-text-primary mb-1"
        >
          Time zone
        </label>
        <p className="text-sm text-warm-text-secondary mb-3">
          JuDI uses this time zone for your calendar. Changing it keeps each
          task at the same clock time.
        </p>
        <select
          id="judi-time-zone"
          value={timezone ?? ""}
          onChange={(event) => {
            if (event.target.value) updateTimezone(event.target.value);
          }}
          className="w-full rounded-lg border border-warm-border bg-warm-bg-card px-3 py-2 text-sm text-warm-text-primary"
        >
          {!timezone && <option value="">Choose a time zone</option>}
          {timeZones.map((zone) => (
            <option key={zone} value={zone}>
              {zone}
            </option>
          ))}
        </select>
      </div>

      {/* Disconnect confirmation dialog */}
      <AnimatePresence>
        {showDisconnectConfirm && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 animate-dialog-backdrop">
          <div
            role="dialog"
            aria-label="Confirm disconnect"
            className="bg-warm-bg-card rounded-xl border border-warm-border p-6 max-w-sm mx-4 animate-dialog-panel"
          >
            <h4 className="text-lg font-semibold text-warm-text-primary mb-2">
              Disconnect Google Calendar?
            </h4>
            <p className="text-sm text-warm-text-secondary mb-4">
              This will stop syncing tasks, remove all calendar event mappings,
              and delete the JuDI calendar from your Google Calendar.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-warm-text-secondary bg-warm-bg-elevated hover:bg-warm-border-subtle rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDisconnect}
                disabled={isDisconnecting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg transition-colors flex items-center gap-2"
              >
                {isDisconnecting && (
                  <Loader2 className="animate-spin" size={14} />
                )}
                Disconnect
              </button>
            </div>
          </div>
        </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CalendarSettings;
